/**
 * Single organisation card with metadata and action menu.
 *
 * Extracted from OrganisationManagementPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import { Building2, Key, Link2, MoreVertical, Users } from 'lucide-react'
import type { Organisation } from '../../types'
import { getStatusBadgeColor, getTypeBadgeColor } from './helpers'
import { OrganisationActionMenu } from './OrganisationActionMenu'

interface OrganisationCardProps {
  org: Organisation
  isMenuOpen: boolean
  onToggleMenu: () => void
  onEdit: (org: Organisation) => void
  onDelete: (org: Organisation) => void
  onToggleStatus: (org: Organisation) => void
}

function CardHeader({
  org,
  isMenuOpen,
  onToggleMenu,
  onEdit,
  onDelete,
  onToggleStatus,
}: OrganisationCardProps): React.ReactElement {
  return (
    <div className="mb-3 flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-stone-100 p-2 dark:bg-stone-700">
          <Building2 className="h-5 w-5 text-stone-600 dark:text-stone-400" />
        </div>
        <div>
          <h3 className="font-medium text-stone-900 dark:text-stone-100">{org.name}</h3>
          <p className="text-sm text-stone-500 dark:text-stone-400">{org.slug}</p>
        </div>
      </div>
      <div className="relative">
        <button
          onClick={onToggleMenu}
          className="rounded p-1 hover:bg-stone-100 dark:hover:bg-stone-700"
        >
          <MoreVertical className="h-5 w-5 text-stone-500" />
        </button>
        {isMenuOpen && (
          <OrganisationActionMenu
            org={org}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleStatus={onToggleStatus}
          />
        )}
      </div>
    </div>
  )
}

function CardBadges({ org }: { org: Organisation }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="mb-3 flex flex-wrap gap-2">
      <span
        className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getTypeBadgeColor(org.orgType)}`}
      >
        {org.orgType}
      </span>
      <span
        className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusBadgeColor(org.status)}`}
      >
        {org.status}
      </span>
      {org.xeroOrganisationId && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-cyan-100 px-2 py-1 text-xs font-medium text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200"
          title={`Xero ID: ${org.xeroOrganisationId}`}
        >
          <Link2 className="h-3 w-3" />
          {t('organisations.xeroLinked')}
        </span>
      )}
    </div>
  )
}

function CardFooter({ org }: { org: Organisation }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <>
      <div className="flex items-center gap-4 text-sm text-stone-500 dark:text-stone-400">
        <div className="flex items-center gap-1">
          <Users className="h-4 w-4" />
          <span>{t('organisations.users')}</span>
        </div>
        <div className="flex items-center gap-1">
          <Key className="h-4 w-4" />
          <span>{t('organisations.licenses')}</span>
        </div>
      </div>
      <div className="mt-3 border-t border-stone-200 pt-3 dark:border-stone-700">
        <p className="text-xs text-stone-500 dark:text-stone-400">
          {t('organisations.created')} {new Date(org.createdAt).toLocaleDateString()}
        </p>
      </div>
    </>
  )
}

export function OrganisationCard(props: OrganisationCardProps): React.ReactElement {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4 dark:border-stone-700 dark:bg-stone-800">
      <CardHeader {...props} />
      <CardBadges org={props.org} />
      <CardFooter org={props.org} />
    </div>
  )
}
